import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ArrowRight, Construction, LineChart } from "lucide-react";
import { reportById } from "@/features/reports/registry";
import type { ReportId } from "@/features/reports/types";

export const Route = createFileRoute("/_authenticated/reports/$reportId")({
  component: ReportStub,
});

function ReportStub() {
  const { reportId } = useParams({ from: "/_authenticated/reports/$reportId" });
  const report = reportById(reportId as ReportId);

  const isPartial = report?.status === "partial";

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" className="mb-2 -ml-2 text-muted-foreground" asChild>
        <Link to="/reports">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Reports
        </Link>
      </Button>
      <Card>
        <CardContent className="space-y-4 p-8 text-center">
          {isPartial ? (
            <LineChart className="mx-auto h-10 w-10 text-primary" />
          ) : (
            <Construction className="mx-auto h-10 w-10 text-muted-foreground" />
          )}
          <div className="flex items-center justify-center gap-2">
            <span className="text-xs font-mono text-muted-foreground">{report?.id ?? reportId}</span>
            <Badge variant={isPartial ? "outline" : "secondary"}>
              {isPartial ? "Partial" : "Coming soon"}
            </Badge>
          </div>
          <h1 className="text-xl font-semibold">{report?.title ?? `Report ${reportId}`}</h1>
          <p className="text-sm text-muted-foreground">{report?.description ?? "This report is not yet available."}</p>

          {isPartial ? (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                {report?.partialNote ?? "A dedicated report page is in progress. The underlying data is available in the linked module."}
              </p>
              {report?.partialHref && (
                <Button asChild>
                  <Link to={report.partialHref}>
                    Open data <ArrowRight className="ml-1 h-3 w-3" />
                  </Link>
                </Button>
              )}
              <p className="text-xs text-muted-foreground">Export and additional columns: coming soon.</p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Coming soon — track the roadmap or request priority via Admin.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
